export type LogLevel = 'log' | 'info' | 'debug' | 'warn' | 'error';

export interface LogEntry {
	timestamp: number;
	level: LogLevel;
	source: string;
	// A lone string arg (the overwhelmingly common case: logger.info('did
	// the thing')) stays a plain string. Anything else -- multiple args, or
	// a single non-string one -- keeps the original argument list intact
	// (objects/Errors as real objects, not pre-stringified), the same shape
	// console.log itself would show.
	message: string | Array<unknown>;
	stack?: string;
	// Call-site of the `logger.error(...)` invocation itself (file:line),
	// not the stack of an Error argument -- lets you check out the version
	// this log came from and look at the exact line that logged it. Only
	// populated for `error`: `warn`/`debug`/etc are too frequent for this to
	// be worth a stack capture on every call.
	location?: string;
	/** Set only on entries rehydrated from the session before a reload, so a
	 * reader of `logs.json` can tell two sessions apart instead of seeing one
	 * confusingly interleaved timeline. Never set on live entries. */
	restored?: true;
}

/** Renders a `LogEntry.message` for display -- used by the devtools panel. */
export function formatLogMessage(message: LogEntry['message']): string {
	if (typeof message === 'string') {
		return message;
	}
	return message.map(formatLogArg).join(' ');
}

function formatLogArg(arg: unknown): string {
	if (arg instanceof Error) {
		return arg.message;
	}
	if (typeof arg === 'object' && arg !== null) {
		try {
			return JSON.stringify(arg);
		} catch {
			return String(arg);
		}
	}
	return String(arg);
}

/**
 * In memory during a session and persisted across a reload (see
 * `persistLogEntries`/`restoreLogEntries` below) -- the verbose breadcrumb
 * trail around the durable events already in `cf-observability`
 * (@shared/observability), not a replacement for it. Feeds the future
 * TanStack Devtools "Canvasflow Debug" panel and the zip export
 * (improvements/17-debug-capture-export.md). Durable event history is
 * still the observability pipeline's job, not this one's: this buffer
 * carries at most the current session plus the one before it.
 *
 * Cap lowered 500 -> 100 (2026-08-25, user call). The sizing question is
 * "how many entries does it take to understand one reproduction", not
 * "how much can we afford to keep": not much happens per page here, so a
 * reproduction is a short burst of entries, and 100 covers it with room
 * to spare. The governing use case is **debugging a browser you don't
 * have access to** -- someone else's device, via the zip export -- where
 * a smaller, denser log is easier to read than a long one padded with
 * whatever else the session did.
 *
 * Note this buffer never touches IndexedDB, so the cap is not a storage
 * decision -- the store with an actual byte budget is `cf-observability`
 * (`storage/eviction.ts`'s `BYTE_BUDGET`), a separate knob.
 */
const MAX_ENTRIES = 100;

let entries: Array<LogEntry> = [];
const listeners = new Set<() => void>();

/**
 * Survives a reload, which the in-memory buffer alone does not. The failure
 * this exists for: a reader hits a bug, the app reloads (or they reload it),
 * and by the time they export, `logs.json` is thin or empty while
 * `events.json` is intact -- losing exactly the breadcrumbs leading up to
 * the failure, which is the part you cannot reconstruct when you can't open
 * DevTools on their machine.
 *
 * `localStorage`, not the Dexie store, on purpose. It is **synchronous**, so
 * the write completes during page teardown; an async IndexedDB write can be
 * killed mid-flight. At 100 entries this is tens of KB against a multi-MB
 * budget. The objection recorded when this was first designed -- that
 * `LocalStorageKV` rewrites a full JSON blob per write -- applied to writing
 * on every log call; this writes twice per session, on the same lifecycle
 * hooks `local-reporter.ts` already uses.
 */
const STORAGE_KEY = 'cf-debug-log';

// Guarded rather than assumed: this module is pulled into the service-worker
// bundle via `logger.ts`, and a SW global scope has no `localStorage` at all.
// Nothing here is called from the SW, but a stray reference must not throw.
function getStorage(): Storage | null {
	try {
		return typeof localStorage === 'undefined' ? null : localStorage;
	} catch {
		return null;
	}
}

function notify() {
	for (const listener of listeners) {
		listener();
	}
}

export function pushLogEntry(entry: LogEntry) {
	// Always a new array, never entries.push() in place: getLogEntries() is
	// used as a useSyncExternalStore snapshot, which decides whether to
	// re-render via Object.is on the returned reference -- mutating in
	// place would leave that reference unchanged and silently skip the
	// re-render despite notify() firing.
	const next = entries.length >= MAX_ENTRIES ? entries.slice(1) : entries;
	entries = [...next, entry];
	notify();
}

export function getLogEntries(): Array<LogEntry> {
	return entries;
}

export function clearLogEntries() {
	entries = [];
	notify();
}

/** For `useSyncExternalStore` in the future devtools panel -- no polling. */
export function subscribeToLogEntries(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/**
 * Writes the *current* session's entries only -- anything already carrying
 * `restored` is dropped rather than re-persisted, so at most one prior
 * session is ever carried forward and it is always the immediately preceding
 * one. Without that filter each reload would compound generations of stale
 * context into the buffer.
 *
 * Failures are swallowed: a full or unavailable quota (private browsing,
 * storage disabled) must not break a page's teardown.
 */
export function persistLogEntries(): void {
	const storage = getStorage();
	if (!storage) return;

	const live = entries.filter((entry) => !entry.restored);
	try {
		if (live.length === 0) {
			storage.removeItem(STORAGE_KEY);
			return;
		}
		storage.setItem(STORAGE_KEY, JSON.stringify(live, errorReplacer));
	} catch {
		// Most likely QuotaExceededError. Nothing useful to do -- the buffer
		// is a best-effort convenience, not the durable record.
	}
}

/**
 * Rehydrates the previous session's entries, tagged `restored`, and clears
 * the stored copy so the same generation can't be picked up twice. Call once
 * at bootstrap, before anything logs, so restored entries sort ahead of the
 * current session's.
 */
export function restoreLogEntries(): void {
	const storage = getStorage();
	if (!storage) return;

	let raw: string | null = null;
	try {
		raw = storage.getItem(STORAGE_KEY);
		storage.removeItem(STORAGE_KEY);
	} catch {
		return;
	}
	if (!raw) return;

	try {
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return;
		const restored = parsed
			.filter((e): e is LogEntry => typeof e === 'object' && e !== null)
			.map((e) => ({ ...e, restored: true as const }));
		if (restored.length === 0) return;
		// Same cap as live pushes: keep the newest if the two together spill.
		entries = [...restored, ...entries].slice(-MAX_ENTRIES);
		notify();
	} catch {
		// Corrupt blob -- already removed above, so it can't recur.
	}
}

// An entry's `message` can carry raw arguments including Error objects, which
// JSON.stringify serializes to `{}` by default (name/message/stack aren't
// enumerable own properties). Mirrors export-debug-zip.ts's replacer so a
// persisted entry survives the round trip as usefully as an exported one.
function errorReplacer(_key: string, value: unknown): unknown {
	if (value instanceof Error) {
		return { name: value.name, message: value.message, stack: value.stack };
	}
	return value;
}
